import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const TooltipContent = styled.div`
  position: absolute;
  z-index: 1000;
  padding: 0.5rem 0.75rem;
  background-color: ${props => props.theme.colors.gray[900] || '#1f2937'};
  color: ${props => props.theme.colors.white || '#ffffff'};
  border-radius: ${props => props.theme.borderRadius.md || '6px'};
  font-size: ${props => props.theme.typography.fontSize.xs || '0.75rem'};
  font-weight: ${props => props.theme.typography.fontWeight.medium || '500'};
  white-space: nowrap;
  pointer-events: none;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  
  ${props => {
    switch (props.position) {
      case 'top':
        return `
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 5px solid transparent;
            border-top-color: ${props.theme.colors.gray[900] || '#1f2937'};
          }
        `;
      case 'bottom':
        return `
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          &::after {
            content: '';
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 5px solid transparent;
            border-bottom-color: ${props.theme.colors.gray[900] || '#1f2937'};
          }
        `;
      case 'left':
        return `
          right: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
          &::after {
            content: '';
            position: absolute;
            left: 100%;
            top: 50%;
            transform: translateY(-50%);
            border: 5px solid transparent;
            border-left-color: ${props.theme.colors.gray[900] || '#1f2937'};
          }
        `;
      case 'right':
        return `
          left: calc(100% + 8px);
          top: 50%;
          transform: translateY(-50%);
          &::after {
            content: '';
            position: absolute;
            right: 100%;
            top: 50%;
            transform: translateY(-50%);
            border: 5px solid transparent;
            border-right-color: ${props.theme.colors.gray[900] || '#1f2937'};
          }
        `;
      default:
        return `
          bottom: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          &::after {
            content: '';
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            border: 5px solid transparent;
            border-top-color: ${props.theme.colors.gray[900] || '#1f2937'};
          }
        `;
    }
  }}
`;

const Tooltip = ({ 
  children, 
  text, 
  position = 'top',
  delay = 200,
  disabled = false 
}) => {
  const [show, setShow] = useState(false);
  const timeoutRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (disabled || !text) return;
    timeoutRef.current = setTimeout(() => {
      setShow(true);
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setShow(false);
  };

  if (!text || disabled) {
    return <>{children}</>;
  }

  return (
    <TooltipWrapper
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <TooltipContent show={show} position={position}>
        {text}
      </TooltipContent>
    </TooltipWrapper>
  );
};

export default Tooltip;


